import type {
  DateThresholdData,
  NumericalThresholdData,
} from '../../../../core/types/thresholds.js';
import { isStatic, isStaticRange } from '../../../../core/types/type-guards.js';
import type {
  XYChartThresholdIndicatorProps,
  XYChartThresholdDynamicProps,
} from '../../../../xy-chart/types/xy-chart-props.js';

export type AxisEntry = { position: 'left' | 'right'; axisId: string };

interface ThresholdInput {
  data: DateThresholdData | NumericalThresholdData;
  color: string;
  label?: string;
  strokeOnly?: boolean;
  position?: 'left' | 'right';
}

function buildPositionToAxisIdMap(
  axisEntries: AxisEntry[],
): Record<string, string> {
  return axisEntries.reduce<Record<string, string>>(
    (map, { position, axisId }) => {
      if (!(position in map)) {
        map[position] = axisId;
      }
      return map;
    },
    {},
  );
}

export function reduceThresholdsToXY(
  thresholds: ThresholdInput[],
  axisEntries: AxisEntry[],
  xAxisId: string,
  mapDynamic: (
    data: DateThresholdData | NumericalThresholdData,
  ) => XYChartThresholdDynamicProps['data'] | null,
): XYChartThresholdIndicatorProps[] {
  const positionToAxisId = buildPositionToAxisIdMap(axisEntries);
  return thresholds.reduce<XYChartThresholdIndicatorProps[]>(
    (result, { data, color, label, strokeOnly, position }) => {
      const yAxisId = positionToAxisId[position ?? 'left'];

      if (!yAxisId) {
        return result;
      }

      const common = { color, label, strokeOnly, yAxisId };

      if (isStatic(data)) {
        result.push({ ...common, data: { y: data.value } });
      } else if (isStaticRange(data)) {
        result.push({ ...common, data: { yMin: data.min, yMax: data.max } });
      } else {
        const dynamicData = mapDynamic(data);
        if (dynamicData !== null) {
          result.push({ ...common, data: dynamicData, xAxisId });
        }
      }

      return result;
    },
    [],
  );
}
